import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AdminAuthService } from '@app/services/admin-auth/admin-auth.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { environment } from '@environments/_current/environment';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';

export interface ChallengeTeamMember {
  id?: string;
  userId?: string;
  name: string;
  score?: number;
  rank?: number;
  iconColor?: string;
  image?: string;
  isCurrentUser?: boolean;
}

export interface ChallengeTeam {
  id?: string;
  teamId?: string | number;
  name: string;
  score?: number;
  rank?: number;
  color?: string;
  iconColor?: string;
  action?: { teamId?: string | number };
  members?: ChallengeTeamMember[] | null;
}

interface ChallengeTeamsResponse {
  title?: string;
  teams?: ChallengeTeam[];
}

interface ChallengeMembersResponse {
  title?: string;
  users?: ChallengeTeamMember[];
  members?: ChallengeTeamMember[];
}

export enum StepChallengeTeamAssignmentType {
  DEFAULT = 'default',
  MAX_TEAM_SIZE = 'max_team_size',
  FIXED_TEAM_COUNT = 'fixed_team_count',
}

export const STEP_CHALLENGE_TEAM_ASSIGNMENT_OPTIONS = [
  {
    type: StepChallengeTeamAssignmentType.DEFAULT,
    label: 'groupChallenges.defaultTeamAssignmentStrategy',
  },
  {
    type: StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT,
    label: 'groupChallenges.fixCountTeamAssignmentStrategy',
  },
  {
    type: StepChallengeTeamAssignmentType.MAX_TEAM_SIZE,
    label: 'groupChallenges.maxTeamAssignmentStrategy',
  },
];

export interface Challenge {
  id: string;
  type: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status?: string;
  goal?: string;
  teams?: ChallengeTeam[] | null;
  joinInProgress?: boolean;
  enrollmentStartDate?: string;
  teamAssignmentType?: string;
  teamAssignmentValue?: number | null;
}

export enum MetricChallengeViewStatus {
  NOT_STARTED = 'notStarted',
  STARTED = 'started',
  ENDED = 'ended',
  NOT_SET = 'notSet',
}

// challenge-api starts and ends challenges at midnight in a fixed UTC-6 offset
// (CronService), so the admin uses the same day boundary for status.
const CHALLENGE_DAY_UTC_OFFSET_HOURS = -6;
const MS_PER_HOUR = 1000 * 60 * 60;

const toCalendarDay = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

const toChallengeToday = (currentDate: Date): number =>
  toCalendarDay(
    new Date(currentDate.getTime() + CHALLENGE_DAY_UTC_OFFSET_HOURS * MS_PER_HOUR)
  );

export const hasChallengeDayArrived = (
  date: Date,
  currentDate = new Date()
): boolean => {
  const day = toCalendarDay(date);
  return !Number.isNaN(day) && toChallengeToday(currentDate) >= day;
};

export const getMetricChallengeStatus = (
  startDate: Date,
  endDate: Date,
  currentDate = new Date()
): MetricChallengeViewStatus => {
  const today = toChallengeToday(currentDate);
  const start = toCalendarDay(startDate);
  const end = toCalendarDay(endDate);

  if ([today, start, end].some(Number.isNaN)) {
    return MetricChallengeViewStatus.NOT_SET;
  }
  if (today < start) {
    return MetricChallengeViewStatus.NOT_STARTED;
  }
  if (today <= end) {
    return MetricChallengeViewStatus.STARTED;
  }
  return MetricChallengeViewStatus.ENDED;
};

export interface CreateChallengePayload {
  type: string;
  name: string;
  description: string;
  goal: string;
  startDate: string;
  endDate: string;
  access: string | string[];
  author: string;
  joinInProgress: boolean;
  enrollmentStartDate: string;
  teamAssignmentType: StepChallengeTeamAssignmentType | string;
  teamAssignmentValue?: number | null;
}

export interface UpdateChallengePayload {
  type?: string;
  name?: string;
  description?: string;
  goal?: string;
  startDate?: string;
  endDate?: string;
  access?: string | string[];
  author?: string;
  joinInProgress?: boolean;
  enrollmentStartDate?: string;
  teamAssignmentType?: StepChallengeTeamAssignmentType | string;
  teamAssignmentValue?: number | null;
}

export interface EditChallengePayload {
  id: string;
  author: string;
  name?: string;
  description?: string;
  type?: string;
  goal?: string;
  startDate?: string;
  endDate?: string;
  enrollmentStartDate?: string;
  joinInProgress?: boolean;
  teamAssignmentType?: string;
  teamAssignmentValue?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class ChallengeService {
  private readonly http = inject(HttpClient);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly adminAuthService = inject(AdminAuthService);

  // endDate is inclusive: a challenge that starts and ends on the same day lasts 1 day.
  dateToDurationUnit = (from: string, to: string) => {
    const dayUnit = 1;
    const weekUnit = 7;
    const inclusiveDayOffset = 1;
    const defaultDuration = 1;

    const fromDate = new Date(from).getTime();
    const toDate = new Date(to).getTime();

    if (Number.isNaN(fromDate) || Number.isNaN(toDate)) {
      return [defaultDuration, dayUnit];
    }

    const msPerDay = 1000 * 60 * 60 * 24;
    const timeDiff = Math.abs(toDate - fromDate);
    const days = Math.round(timeDiff / msPerDay) + inclusiveDayOffset;

    const isWeek = days % weekUnit === 0;
    let duration = days;
    if (isWeek) {
      duration = days / weekUnit;
    }

    return [duration, isWeek ? weekUnit : dayUnit];
  };

  getTeamId(team: {
    teamId?: string | number;
    id?: string | number;
    action?: { teamId?: string | number };
  }): string {
    return String(team.teamId ?? team.id ?? team.action?.teamId ?? '');
  }

  getChallengeTeams(challengeId: string): Observable<ChallengeTeam[]> {
    const url = `${environment.aaptivServiceUrl}/api/v1/user/groupChallenge/${challengeId}/teams`;
    return this.http
      .get<ChallengeTeam[] | ChallengeTeamsResponse>(url, {
        params: this.withExternalUserId(),
      })
      .pipe(
        map((res) =>
          (Array.isArray(res) ? res : res.teams ?? []).map((team) =>
            this.normalizeTeam(team)
          )
        )
      );
  }

  getChallengeMembers(
    challengeId: string,
    teamId: string
  ): Observable<ChallengeTeamMember[]> {
    const url = `${environment.aaptivServiceUrl}/api/v2/user/groupChallenge/${challengeId}/members`;
    const params = this.withExternalUserId().set('teamId', teamId);
    return this.http
      .get<ChallengeTeamMember[] | ChallengeMembersResponse>(url, { params })
      .pipe(
        map((res) => {
          const members = Array.isArray(res)
            ? res
            : res.users ?? res.members ?? [];
          return members.map((member) => this.normalizeMember(member));
        })
      );
  }

  getChallengeTeamsWithMembers(
    challengeId: string
  ): Observable<ChallengeTeam[]> {
    return this.getChallengeTeams(challengeId).pipe(
      switchMap((teams) => {
        if (teams.length === 0) {
          return of([]);
        }
        return forkJoin(
          teams.map((team) => {
            const teamId = this.getTeamId(team);
            if (!teamId) {
              return of({ ...team, members: [] });
            }
            return this.getChallengeMembers(challengeId, teamId).pipe(
              map((members) => ({ ...team, members })),
              catchError(() => of({ ...team, members: [] }))
            );
          })
        );
      })
    );
  }

  private withExternalUserId(params = new HttpParams()): HttpParams {
    const userId = this.adminAuthService.getUserId();
    return userId ? params.set('externalUserId', userId) : params;
  }

  private normalizeTeam(team: ChallengeTeam): ChallengeTeam {
    const teamId = this.getTeamId(team);
    return {
      ...team,
      id: team.id ?? teamId,
      teamId: team.teamId ?? teamId,
      name: team.name ?? '',
      score: team.score ?? 0,
      rank: team.rank ?? 0,
      color: team.color ?? team.iconColor ?? '#1100DB',
      members: team.members ?? [],
    };
  }

  private normalizeMember(member: ChallengeTeamMember): ChallengeTeamMember {
    return {
      ...member,
      id: member.id ?? member.userId,
      name: member.name ?? '',
      score: member.score ?? 0,
    };
  }

  createChallenge(challenge: CreateChallengePayload) {
    const url = `${environment.aaptivServiceUrl}/api/v1/challenges`;

    const create: CreateChallengePayload = {
      ...challenge,
    };

    return this.http.post<Challenge>(url, create);
  }

  getChallenge(id: string) {
    const url = `${environment.aaptivServiceUrl}/api/v1/challenges/${id}`;
    return this.http.get<Challenge>(url);
  }

  getStepChallenge(id: string) {
    const tenantId = this.adminTenantService.getTenantId();
    return this.searchMetricChallenges(tenantId).pipe(
      map((challenges) =>
        challenges.find((challenge) => String(challenge.id) === String(id))
      )
    );
  }

  searchMetricChallenges(tenantId: string): Observable<Challenge[]> {
    const url = `${environment.aaptivServiceUrl}/api/v1/challenges/${tenantId}`;
    return this.http
      .get<Challenge[] | { metricChallenges?: Challenge[] }>(url)
      .pipe(
        map((res) => (Array.isArray(res) ? res : res.metricChallenges ?? []))
      );
  }

  updateChallenge(id: string, challenge: UpdateChallengePayload) {
    const url = `${environment.aaptivServiceUrl}/api/v1/challenges/${id}`;
    return this.http.patch<Challenge>(url, challenge);
  }

  editChallenge(payload: EditChallengePayload) {
    const url = `${environment.aaptivServiceUrl}/api/v1/challenges/edit`;
    return this.http.post<Challenge>(url, payload);
  }

  deleteChallenge(id: string) {
    const url = `${environment.aaptivServiceUrl}/api/v1/challenges/${id}`;
    return this.http.delete(url);
  }
}
